import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  LinearProgress,
  Card,
  CardContent,
  Autocomplete,
  useTheme,
  alpha,
  Stack,
  Divider,
  Fab,
  Tooltip,
} from "@mui/material";
import {
  PhotoCamera,
  Close,
  Add,
  Person,
  Language,
  LocationOn,
  Interests,
  Security,
  Edit,
  CloudUpload,
  Star,
  Public,
  Lock,
  Save,
  Camera,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useApp } from "../../contexts/AppContext";
import { updateUser, uploadFile, getFileUrl } from "../../services/api";
import { v4 as uuidv4 } from "uuid";
import { normalizeLanguagePreferences, SUPPORTED_LANGUAGES } from '../../utils/languageUtils';

const EditProfile = ({ open, onClose, user, onUpdate }) => {
  const { user: currentUser, updateUserProfile } = useAuth();
  const { addNotification } = useApp();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState({
    displayName: user?.displayName || "",
    bio: user?.bio || "",
    website: user?.website || "",
    location: user?.location || "",
    interests: user?.interests || [],
    languagePreferences: normalizeLanguagePreferences(user?.languagePreferences) || [],
    watchlistPublic: user?.watchlistPublic !== undefined ? user?.watchlistPublic : true,
  });
  
  const [errors, setErrors] = useState({});

  const getKeyFromUrl = (url) => {
    if (!url) return "";
    if (url.includes("public/")) {
      return url.substring(url.indexOf("public/") + 7);
    }
    return url;
  };

  const [newInterest, setNewInterest] = useState("");
  const [avatar, setAvatar] = useState(getKeyFromUrl(user?.avatar));
  const [coverPhoto, setCoverPhoto] = useState(getKeyFromUrl(user?.coverPhoto));
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  const [coverPhotoPreviewUrl, setCoverPhotoPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const avatarInputRef = useRef();
  const coverInputRef = useRef();

  React.useEffect(() => {
    const loadPreviewUrls = async () => {
      if (avatar) {
        try {
          const url = await getFileUrl(avatar, null, 'public');
          setAvatarPreviewUrl(url);
        } catch (error) {
          // Handle error silently
        }
      }
      if (coverPhoto) {
        try {
          const url = await getFileUrl(coverPhoto, null, 'public');
          setCoverPhotoPreviewUrl(url);
        } catch (error) {
          // Handle error silently
        }
      }
    };
    loadPreviewUrls();
  }, [avatar, coverPhoto]);

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'displayName':
        if (!value.trim()) {
          newErrors.displayName = 'Display name is required';
        } else if (value.trim().length < 2) {
          newErrors.displayName = 'Display name must be at least 2 characters';
        } else if (value.length > 50) {
          newErrors.displayName = 'Display name must be less than 50 characters';
        } else {
          delete newErrors.displayName;
        }
        break;
      case 'bio':
        if (value.length > 500) {
          newErrors.bio = 'Bio must be less than 500 characters';
        } else {
          delete newErrors.bio;
        }
        break;
      case 'website':
        if (value && !/^https?:\/\/.+/.test(value)) {
          newErrors.website = 'Website must start with http:// or https://';
        } else {
          delete newErrors.website;
        }
        break;
      case 'location':
        if (value.length > 100) {
          newErrors.location = 'Location must be less than 100 characters';
        } else {
          delete newErrors.location;
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    
    if (type !== "checkbox") {
      validateField(name, newValue);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      addNotification({
        type: "error",
        message: "Avatar image must be less than 5MB",
      });
      return;
    }

    try {
      setLoading(true);
      const key = `avatars/${currentUser.username}/${uuidv4()}-${file.name}`;
      const uploadedKey = await uploadFile(
        file,
        key,
        (progress) => setUploadProgress(progress),
        "public"
      );
      setAvatar(uploadedKey);
      const url = await getFileUrl(uploadedKey, null, 'public');
      setAvatarPreviewUrl(url);
      setTimeout(() => setUploadProgress(0), 1000);
    } catch {
      addNotification({
        type: "error",
        message: "Failed to upload avatar",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      addNotification({
        type: "error",
        message: "Cover photo must be less than 10MB",
      });
      return;
    }

    try {
      setLoading(true);
      const key = `covers/${currentUser.username}/${uuidv4()}-${file.name}`;
      const uploadedKey = await uploadFile(
        file,
        key,
        (progress) => setUploadProgress(progress),
        "public"
      );
      setCoverPhoto(uploadedKey);
      const url = await getFileUrl(uploadedKey, null, 'public');
      setCoverPhotoPreviewUrl(url);
      setTimeout(() => setUploadProgress(0), 1000);
    } catch {
      addNotification({
        type: "error",
        message: "Failed to upload cover photo",
      });
    } finally {
      setLoading(false);
    }
  };

  const addInterest = () => {
    if (newInterest.trim() && !formData.interests.includes(newInterest.trim())) {
      setFormData((prev) => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim()],
      }));
      setNewInterest("");
    }
  };

  const removeInterest = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.filter((i) => i !== interest),
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const updateData = {
        ...formData,
        avatar: avatar !== getKeyFromUrl(user?.avatar) ? avatar : undefined,
        coverPhoto: coverPhoto !== getKeyFromUrl(user?.coverPhoto) ? coverPhoto : undefined,
      };

      Object.keys(updateData).forEach(
        (key) => updateData[key] === undefined && delete updateData[key]
      );

      const updatedUser = await updateUserProfile(updateData);
      onUpdate(updatedUser);

      addNotification({
        type: "success",
        message: "Profile updated successfully!",
      });

      onClose();
    } catch {
      addNotification({
        type: "error",
        message: "Failed to update profile",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.displayName.trim().length >= 2 && 
                      formData.displayName.length <= 50 &&
                      formData.bio.length <= 500 &&
                      formData.location.length <= 100 &&
                      Object.keys(errors).length === 0;

  const primaryColor = isDark ? '#ffd700' : '#1976d2';
  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #ffd700 0%, #ffb300 100%)'
    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';

  const cardStyle = {
    background: isDark 
      ? 'rgba(30, 30, 30, 0.95)' 
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(primaryColor, 0.2)}`,
    borderRadius: 3,
    boxShadow: isDark
      ? '0 8px 32px rgba(0, 0, 0, 0.3)'
      : '0 8px 32px rgba(0, 0, 0, 0.1)',
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={theme.breakpoints.down('sm')}
      PaperProps={{
        sx: {
          ...cardStyle,
          overflow: 'hidden',
          maxHeight: { xs: '100vh', sm: '90vh' },
          margin: { xs: 0, sm: 2 },
        }
      }}
    >
      {uploadProgress > 0 && (
        <LinearProgress 
          variant="determinate" 
          value={uploadProgress}
          sx={{
            height: 3,
            background: alpha(primaryColor, 0.2),
            '& .MuiLinearProgress-bar': {
              background: primaryGradient,
            }
          }}
        />
      )}

      {/* Header with Cover Photo */}
      <Box sx={{ position: 'relative', height: { xs: 200, sm: 240 }, flexShrink: 0 }}>
        {/* Cover Photo Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: coverPhotoPreviewUrl
              ? `url(${coverPhotoPreviewUrl})`
              : primaryGradient,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
            }
          }}
        />

        {/* Header Content */}
        <Box sx={{ 
          position: 'relative', 
          zIndex: 2, 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <Typography variant="h5" sx={{ 
            color: 'white', 
            fontWeight: 700,
            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
          }}>
            Edit Profile
          </Typography>
          
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Cover Photo Upload Button */}
        <Tooltip title="Upload cover photo (max 10MB)" arrow>
          <Fab
            size="small"
            onClick={() => coverInputRef.current?.click()}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              zIndex: 2,
              background: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.8)',
              }
            }}
          >
            <PhotoCamera />
          </Fab>
        </Tooltip>

        {/* Avatar */}
        <Box sx={{
          position: 'absolute',
          bottom: -50,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
        }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={avatarPreviewUrl}
              sx={{
                width: 100,
                height: 100,
                border: `4px solid ${isDark ? '#1e1e1e' : '#fff'}`,
                cursor: 'pointer',
                fontSize: 36,
                fontWeight: 700,
                background: primaryGradient,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
              onClick={() => avatarInputRef.current?.click()}
            >
              {formData.displayName?.[0]?.toUpperCase()}
            </Avatar>
            
            <Tooltip title="Upload profile picture (max 5MB)" arrow>
              <Fab
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: -8,
                  right: -8,
                  width: 32,
                  height: 32,
                  minHeight: 32,
                  background: primaryGradient,
                  color: 'white',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  }
                }}
                onClick={() => avatarInputRef.current?.click()}
              >
                <PhotoCamera sx={{ fontSize: 16 }} />
              </Fab>
            </Tooltip>
          </Box>
        </Box>

        <input
          type="file"
          accept="image/*"
          ref={coverInputRef}
          style={{ display: 'none' }}
          onChange={handleCoverUpload}
          disabled={loading}
        />
        <input
          type="file"
          accept="image/*"
          ref={avatarInputRef}
          style={{ display: 'none' }}
          onChange={handleAvatarUpload}
          disabled={loading}
        />
      </Box>

      <DialogContent sx={{ pt: 8, pb: 2, overflowY: 'auto', flex: 1 }}>
        <Stack spacing={3}>
          {/* Basic Info */}
          <Card sx={cardStyle}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Person sx={{ color: primaryColor }} />
                <Typography variant="h6" fontWeight={600}>
                  Basic Information
                </Typography>
              </Box>
              
              <Stack spacing={2}>
                <Tooltip title="Your public display name (2-50 characters)" arrow placement="top">
                  <TextField
                    name="displayName"
                    label="Display Name"
                    fullWidth
                    value={formData.displayName}
                    onChange={handleChange}
                    disabled={loading}
                    size="small"
                    error={!!errors.displayName}
                    helperText={errors.displayName || `${formData.displayName.length}/50`}
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Tooltip>

                <Tooltip title="Share a brief description about yourself (max 500 characters)" arrow placement="top">
                  <TextField
                    name="bio"
                    label="Bio"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.bio}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Tell us about yourself..."
                    size="small"
                    error={!!errors.bio}
                    helperText={errors.bio || `${formData.bio.length}/500`}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Tooltip>
                
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: alpha(primaryColor, 0.05), 
                  borderRadius: 2,
                  border: `1px solid ${alpha(primaryColor, 0.1)}`,
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    color: primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    💡 Profile Tips
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    • Use a clear, friendly profile picture<br/>
                    • Write a bio that shows your personality<br/>
                    • Add your location to connect with nearby users<br/>
                    • Share your website or social links
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Your website or portfolio URL (must start with http:// or https://)" arrow placement="top">
                    <TextField
                      name="website"
                      label="Website"
                      fullWidth
                      value={formData.website}
                      onChange={handleChange}
                      disabled={loading}
                      size="small"
                      error={!!errors.website}
                      helperText={errors.website}
                      placeholder="https://example.com"
                      InputProps={{
                        startAdornment: <Language sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Your city or country (max 100 characters)" arrow placement="top">
                    <TextField
                      name="location"
                      label="Location"
                      fullWidth
                      value={formData.location}
                      onChange={handleChange}
                      disabled={loading}
                      size="small"
                      error={!!errors.location}
                      helperText={errors.location || `${formData.location.length}/100`}
                      inputProps={{ maxLength: 100 }}
                      InputProps={{
                        startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Tooltip>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Languages */}
          <Card sx={cardStyle}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Language sx={{ color: primaryColor }} />
                <Typography variant="h6" fontWeight={600}>
                  Languages
                </Typography>
              </Box>

              <Tooltip title="Select your preferred languages for content recommendations" arrow placement="top">
                <Autocomplete
                  multiple
                  size="small"
                  options={SUPPORTED_LANGUAGES.map(lang => lang.name)}
                  value={formData.languagePreferences}
                  onChange={(event, newValue) => {
                    const normalizedLanguages = newValue.map(lang => {
                      if (lang === 'Hindi' || lang === 'HI' || lang === 'hi') return 'Hindi';
                      if (lang === 'English' || lang === 'EN' || lang === 'en') return 'English';
                      return lang;
                    });
                    setFormData(prev => ({ ...prev, languagePreferences: normalizedLanguages }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="filled"
                        label={option}
                        {...getTagProps({ index })}
                        key={`${option}-${index}`}
                        size="small"
                        sx={{
                          background: primaryGradient,
                          color: 'white',
                          fontWeight: 500,
                          '& .MuiChip-deleteIcon': {
                            color: 'rgba(255, 255, 255, 0.8)',
                            '&:hover': {
                              color: 'white',
                            }
                          }
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Languages"
                      placeholder="Choose languages..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  )}
                />
              </Tooltip>
              
              <Box sx={{ 
                mt: 2,
                p: 2, 
                backgroundColor: alpha(primaryColor, 0.05), 
                borderRadius: 2,
                border: `1px solid ${alpha(primaryColor, 0.1)}`,
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  color: primaryColor,
                }}>
                  🌍 Why add languages?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  We'll show you movies and shows in your preferred languages first, helping you discover content that matches your taste!
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Interests */}
          <Card sx={cardStyle}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Interests sx={{ color: primaryColor }} />
                  <Typography variant="h6" fontWeight={600}>
                    Interests
                  </Typography>
                </Box>
                <Chip 
                  label={`${formData.interests.length}/10`} 
                  size="small" 
                  sx={{ 
                    background: primaryGradient,
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {formData.interests.map((interest) => (
                    <Chip
                      key={interest}
                      label={interest}
                      onDelete={() => removeInterest(interest)}
                      size="small"
                      sx={{
                        background: primaryGradient,
                        color: 'white',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            color: 'white',
                          }
                        }
                      }}
                    />
                  ))}
                </Box>

                {formData.interests.length === 0 && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 3, 
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}>
                    <Star sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2">
                      No interests added yet
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Add your interests (movies, hobbies, travel, etc.) - max 10" arrow placement="top">
                  <TextField
                    placeholder="Add interest..."
                    fullWidth
                    size="small"
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addInterest();
                      }
                    }}
                    disabled={loading || formData.interests.length >= 10}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Tooltip>
                <Button
                  variant="contained"
                  onClick={addInterest}
                  disabled={loading || formData.interests.length >= 10 || !newInterest.trim()}
                  sx={{
                    borderRadius: 2,
                    minWidth: 'auto',
                    px: 2,
                    background: primaryGradient,
                    '&:hover': {
                      background: isDark
                        ? 'linear-gradient(135deg, #e6c200 0%, #f0d000 100%)'
                        : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    }
                  }}
                >
                  <Add />
                </Button>
              </Box>
              
              <Box sx={{ 
                mt: 2,
                p: 2, 
                backgroundColor: alpha(primaryColor, 0.05), 
                borderRadius: 2,
                border: `1px solid ${alpha(primaryColor, 0.1)}`,
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  color: primaryColor,
                }}>
                  ✨ Make connections
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Adding interests helps other users with similar tastes find and connect with you. Think movies, hobbies, travel, food, etc.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Privacy */}
          <Card sx={cardStyle}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Security sx={{ color: primaryColor }} />
                <Typography variant="h6" fontWeight={600}>
                  Privacy
                </Typography>
              </Box>

              <Tooltip title="Control who can see your watchlist - public or private" arrow placement="top">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.watchlistPublic}
                      onChange={handleChange}
                      name="watchlistPublic"
                      disabled={loading}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: primaryColor,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: primaryColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {formData.watchlistPublic ? <Public /> : <Lock />}
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Public Watchlist
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.watchlistPublic 
                            ? 'Others can see your watchlist'
                            : 'Your watchlist is private'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
              </Tooltip>
              
              <Box sx={{ 
                mt: 2,
                p: 2, 
                backgroundColor: alpha(primaryColor, 0.05), 
                borderRadius: 2,
                border: `1px solid ${alpha(primaryColor, 0.1)}`,
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  color: primaryColor,
                }}>
                  🔒 Your privacy matters
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  You can always change these settings later. Public watchlists help others discover great content through your recommendations.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box sx={{ 
        p: 2, 
        borderTop: `1px solid ${alpha(primaryColor, 0.1)}`,
        display: 'flex',
        gap: 2,
        background: alpha(primaryColor, 0.05),
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          fullWidth
          sx={{ 
            borderRadius: 2,
            borderColor: alpha(primaryColor, 0.3),
            color: 'text.secondary',
            '&:hover': {
              borderColor: primaryColor,
              backgroundColor: alpha(primaryColor, 0.05),
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
          variant="contained"
          fullWidth
          sx={{
            borderRadius: 2,
            background: primaryGradient,
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              background: isDark
                ? 'linear-gradient(135deg, #e6c200 0%, #f0d000 100%)'
                : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            }
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default EditProfile;